import { z } from 'zod';
import { currencyCodeSchema, dateOnlySchema } from './common';
import { REPORT_BASES } from './reports';
/**
 * The practising accountant's own surface: many clients at once, one customer's
 * whole story, and the log of who did what.
 *
 * ── THE ONE THING TO KNOW ABOUT THE PRACTICE VIEW ──
 * It is the first thing in Fint that spans organisations, and it is built from
 * the caller's own `memberships` rows - never from a scan of `orgs`. Every
 * per-client figure still goes through `verifyAccess`. A cross-org endpoint
 * that forgets either of those is the whole tenancy model undone in one
 * handler, so it is stated here as well as in the service.
 */
export const practiceQuerySchema = z
    .object({
    /**
     * The period each client's review and return figures are computed over.
     * Absent, the service uses the current month - which is what a CA looking
     * at the list in the first week of the month wants.
     */
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    /**
     * Running fourteen checks across forty clients is forty period loads. Off
     * by default so the list itself is instant, and on when a CA asks
     * "which of my clients has something wrong this month".
     */
    withFindings: z.coerce.boolean().default(false),
})
    .strict();
// ── The overview: every business, one figure each ──────────────────────────
/**
 * How the whole portfolio is doing. Built from the same membership rule as
 * the client list - the org set is the caller's own `memberships` rows (or,
 * over MCP, the orgs a connection was consented for), and each org's figures
 * are computed ONE AT A TIME through `verifyAccess` and the reports service.
 * Never one query across tenants: a wrong `inArray` here is every business's
 * income in one number nobody asked for.
 *
 * ── MONEY IS SUMMED. GST NEVER IS ──
 * A GST-registered LLP and a freelance book with GST off have nothing to add
 * together on a return, and a "combined GST" figure would be a number with no
 * meaning that reads as a filing. The response carries `gstRegime` per org and
 * no GST figure at all. Income, expense and profit are summed only across orgs
 * in one currency; the rest are named in `caveats`, never converted.
 */
export const overviewQuerySchema = z
    .object({
    /** Absent: the twelve months ending today. */
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    basis: z.enum(REPORT_BASES).default('accrual'),
    /** Only the books in this organisation. Absent: every book the person can see. */
    organisationId: z.string().trim().max(36).optional(),
})
    .strict()
    .refine((v) => !v.from || !v.to || v.from <= v.to, {
    path: ['to'],
    message: 'The end of the range comes before its start',
});
// ── Customer ledger ────────────────────────────────────────────────────────
/**
 * A statement is in ONE currency. A customer invoiced in pounds and rupees
 * has two statements; `currency` picks one, defaulting to the customer's
 * own, and the response lists the others it could show.
 */
export const ledgerQuerySchema = z
    .object({ from: dateOnlySchema.optional(), to: dateOnlySchema.optional(), currency: currencyCodeSchema.optional() })
    .strict();
// ── Audit trail ────────────────────────────────────────────────────────────
export const auditQuerySchema = z
    .object({
    entityType: z.string().trim().max(40).optional(),
    entityId: z.string().trim().max(36).optional(),
    actorId: z.string().trim().max(36).optional(),
    action: z.string().trim().max(80).optional(),
    /**
     * Groups of actions, by name. See AUDIT_CATEGORIES.
     *
     * Comma-separated because this arrives in a query string and one repeated
     * parameter versus a list is the sort of difference that works in one HTTP
     * client and not the next.
     */
    categories: z
        .string()
        .trim()
        .max(200)
        .optional()
        .transform((v) => (v ? v.split(',').map((c) => c.trim()).filter(Boolean) : undefined)),
    /**
     * Free text over the action, the person and the record id.
     *
     * Here rather than in the browser because the page holds one page of rows:
     * filtering those is a search that quietly misses everything older, and
     * reports the miss as "no results".
     */
    q: z.string().trim().max(200).optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** `createdAt` of the last row seen, for the next page. */
    before: z.string().trim().max(40).optional(),
})
    .strict();
//# sourceMappingURL=practice.js.map